import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, type ComponentProps, type CSSProperties } from "react";

import {
  Form,
  FormField,
  FormikErrorMessages,
  FormikForm,
  GridTwoColumns,
  Icon,
  SkiliticsIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@skilitics/designer-ui-components";
import {
  getDisabledOffsetOriginsForSliderDirection,
  getInsetValuesForOffsetOriginChange,
  getNearestAllowedOffsetOrigin,
  getSliderConflictingInsetProperties,
  getXYFieldMapping,
  negateCssValue,
  type OffsetOrigin,
  type SliderDirection,
} from "@skilitics/drag-drop";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useEditingCss } from "./EditingCssPropertiesContext.ts";
import { FieldCssInput } from "./FieldCssInput.tsx";
import { OffsetOriginGrid } from "./OffsetOriginGrid.tsx";

type PositionMode = "absolute" | "fixed";
type AbsolutePositionFormValues = Pick<CSSProperties, "top" | "right" | "bottom" | "left">;

const Container = styled.div`
  display: flex;
  gap: 15px;
  align-items: start;
`;

const InsetFieldsContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledToggleButton = styled(ToggleButton)`
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px !important;
  height: 24px;
  width: 24px;
  font-size: 15px;
  padding: 0;
  color: ${({ theme }) => theme.generalTextIcon};
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PositionModePickerContainer = styled.div`
  margin-bottom: 6px; // Gets the input border to line up with the bottom of the grid.
`;

export const AbsolutePositionForm = ({ sliderDirection }: { sliderDirection?: SliderDirection }) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, layoutSettings, onChange, onCssPropertiesChange } = useEditingCss();

  const positionMode: PositionMode = (cssProperties.position as PositionMode) ?? "absolute";

  const handlePositionModeChange: ComponentProps<typeof ToggleButtonGroup>["onChange"] = (_e, value) => {
    if (value && value !== positionMode) {
      onCssPropertiesChange({ position: value as PositionMode });
    }
  };

  const offsetOrigin: OffsetOrigin = layoutSettings.offsetOrigin ?? "topLeft";

  const xyMapping = useMemo(() => getXYFieldMapping(offsetOrigin), [offsetOrigin]);

  const insetFields = useMemo(() => {
    const sliderConflicting = sliderDirection ? getSliderConflictingInsetProperties(sliderDirection) : undefined;
    return [
      {
        property: xyMapping.x,
        label: "X",
        disabled: !!sliderConflicting?.has(xyMapping.x),
        tooltip: xyMapping.horizontalCenter
          ? formatMessage("horizontalOffset")
          : formatMessage(xyMapping.x === "left" ? "leftOffset" : "rightOffset"),
      },
      {
        property: xyMapping.y,
        label: "Y",
        disabled: !!sliderConflicting?.has(xyMapping.y),
        tooltip: xyMapping.verticalCenter
          ? formatMessage("verticalOffset")
          : formatMessage(xyMapping.y === "top" ? "topOffset" : "bottomOffset"),
      },
    ];
  }, [xyMapping, sliderDirection, formatMessage]);

  const disabledOffsetOrigins = useMemo(
    () => (sliderDirection ? getDisabledOffsetOriginsForSliderDirection(sliderDirection) : undefined),
    [sliderDirection],
  );

  const initialValues: AbsolutePositionFormValues = useMemo(
    () => ({
      top: cssProperties.top,
      right: cssProperties.right,
      bottom: cssProperties.bottom,
      left: cssProperties.left,
    }),
    [cssProperties.top, cssProperties.right, cssProperties.bottom, cssProperties.left],
  );

  const handleSubmit = useCallback(
    (values: AbsolutePositionFormValues) => {
      const result = { ...values };
      if (xyMapping.horizontalCenter) {
        result.right = negateCssValue(result.left as string | undefined);
      }
      if (xyMapping.verticalCenter) {
        result.bottom = negateCssValue(result.top as string | undefined);
      }
      onCssPropertiesChange(result);
    },
    [onCssPropertiesChange, xyMapping],
  );

  const handleOffsetOriginChange = useCallback(
    (newOffsetOrigin: OffsetOrigin) => {
      const currentInset = {
        top: cssProperties.top,
        right: cssProperties.right,
        bottom: cssProperties.bottom,
        left: cssProperties.left,
      };
      onChange({
        cssProperties: getInsetValuesForOffsetOriginChange(currentInset, newOffsetOrigin),
        layoutSettings: { offsetOrigin: newOffsetOrigin },
      });
    },
    [onChange, cssProperties.top, cssProperties.right, cssProperties.bottom, cssProperties.left],
  );

  useEffect(() => {
    if (!sliderDirection) return;
    const corrected = getNearestAllowedOffsetOrigin(offsetOrigin, sliderDirection);
    if (corrected !== offsetOrigin) handleOffsetOriginChange(corrected);
  }, [sliderDirection]); // eslint-disable-line react-hooks/exhaustive-deps -- correct offset origin when slider direction changes

  return (
    <Container>
      <OffsetOriginGrid
        activeOffsetOrigin={offsetOrigin}
        onOffsetOriginChange={handleOffsetOriginChange}
        disabledOffsetOrigins={disabledOffsetOrigins}
        disabledOffsetOriginTooltip={sliderDirection ? formatMessage("controlledBySlider") : undefined}
      />

      <InsetFieldsContainer>
        <PositionModePickerContainer>
          <StyledToggleButtonGroup onChange={handlePositionModeChange} value={positionMode} exclusive color="primary">
            <Tooltip title={formatMessage("offsetFromParentDescription")} placement="top">
              <StyledToggleButton value="absolute">
                <SkiliticsIcon icon="context-toolbar-parent-container" />
              </StyledToggleButton>
            </Tooltip>
            <Tooltip title={formatMessage("offsetFromScreenDescription")} placement="top">
              <StyledToggleButton value="fixed">
                <Icon icon="browser" />
              </StyledToggleButton>
            </Tooltip>
          </StyledToggleButtonGroup>
        </PositionModePickerContainer>
        <FormikForm<AbsolutePositionFormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
          submitOnBlur
          dataTest="absolute-position-form"
          validateOnChange={false}
        >
          <Form>
            <GridTwoColumns>
              {insetFields.map(({ property, label, disabled, tooltip }) => (
                <FormField<AbsolutePositionFormValues> key={property} name={property}>
                  <FieldCssInput
                    label={label}
                    tooltip={disabled && sliderDirection ? formatMessage("controlledBySlider") : tooltip}
                    disabled={disabled}
                  />
                </FormField>
              ))}
            </GridTwoColumns>
            <FormikErrorMessages />
          </Form>
        </FormikForm>
      </InsetFieldsContainer>
    </Container>
  );
};

const formatStrings = {
  topOffset: { en: "Vertical Offset from Top" },
  rightOffset: { en: "Horizontal Offset from Right" },
  bottomOffset: { en: "Vertical Offset from Bottom" },
  leftOffset: { en: "Horizontal Offset from Left" },
  horizontalOffset: { en: "Horizontal Offset from Center" },
  verticalOffset: { en: "Vertical Offset from Center" },
  offsetFromParentDescription: { en: "Offset from Parent Container" },
  offsetFromScreenDescription: { en: "Offset from Screen" },
  controlledBySlider: { en: "Controlled by slider direction" },
} satisfies FormatMessages;
